import { addMonths, formatDate, subMonths } from 'date-fns';
import { ApplicationEnv } from './models/application-env.model';
import { Feature } from './models/page-config.models';
import { DateFilter, DateFilterTab, VisualData } from './models/date-filter.model';
import { WidgetType } from './models/widget.model';

export const PROJECT_ID_PREFIX = 'proj-';
export const PROJECT_ID_PLACEHOLDER = ':projectId';

export const ENV: ApplicationEnv = import.meta.env as ApplicationEnv;

export const API_BASE = ENV.VITE_API_BASE_URL;

export const LEGACY_APP_URL = ENV.VITE_LEGACY_APP_URL;

export const INTERCOM_SECRET_KEY = 'Eu0RG94_BIDsn5EnakOtOg3FzKprEfPuilPaS0Ms';

export const DATE_FORMAT = 'dd MMM yy';
export const MONTH_YEAR_FORMAT = 'MMM yyyy';
export const YEAR_FORMAT = 'yyyy';
export const DATE_INPUT_FORMAT = 'MM/dd/yyyy';
export const EXCEL_EXPORT_DATE_FORMAT = 'yyyy-MM-dd';
export const CAPTURE_CURRENT_DATE_FORMAT = 'yyyy-MM-dd';
export const QUARTER_YEAR_FORMAT = 'Q - yyyy';
export const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?/;

export const DASHBOARD_PAGE = '-dashboard-page';

export const ELSIE_AI_V2_CHAT_SHOW_QUERY = ENV.VITE_ELSIE_AI_V2_CHAT_SHOW_QUERY === 'true';
export const ELSIE_AI_V2_CHAT_SHOW_BRANCH = ENV.VITE_ELSIE_AI_V2_CHAT_SHOW_BRANCH === 'true';

export const API = {
  ACTIVITY: 'activity',
  ALIGNMENT: 'question/alignment',
  CHAT: 'insightsChat',
  CHAT_HISTORY_SESSIONS: 'chatHistory/sessions',
  COACH_ME: 'coachMe',
  DOWNLOAD: 'download',
  EXPORT: 'export',
  FEATURE_FLAGS: 'feature-flag/get-flags',
  FEEDBACK: 'feedback',
  FIELD_TEAM: 'field-team',
  FILE_RESPONSES: 'files/responses',
  FILE_UPLOAD: 'fileupload',
  FILTERS: 'filter',
  GET_RESPONSE_IMAGE: 'files/response/image',
  GET_USER_APP_CONFIG: 'user-data/get-user-app-config',
  IMAGE: '/image',
  INSIGHTS_DETAIL: 'insights-detail',
  INTERACTION: 'interaction',
  MULTI_COURCE: 'multisource',
  PAGE_CONFIG: 'user-data/get-user-page-config',
  POWER_BI_WORKSPACES: 'powerbi/workspaces',
  PROFILE: 'profile',
  PROFILE_CAPTURE_DETAILS: 'profile/external',
  PROMPT_IDEAS: 'promptIdeas',
  QUICKVIEW: 'quick-view/info',
  QUICKVIEW_ALIGNMENTS: 'quick-view/alignments',
  QUICKVIEW_INSIGHTS: 'quick-view/insights',
  QUICKVIEW_INTERACTIONS: 'quick-view/interactions',
  REPORT: 'reports',
  SUBMIT: 'submit',
  SUMMARIZE: 'insightsSummarize',
  SURVEY: 'question/survey',
  SURVEYS: 'get-surveys',
  TAGS: 'tag',
  TEAMS: 'teams',
  TOPICS: 'topics',
  USER_DATA: 'user-data',
  USER_FILES: 'files',
  WRITING_ASSISTANT: 'writingAssistant',
};

export const USER_FOOTER_SIDE_NAV_CLASS = 'kwello-auth-user';

export const DATE_DROPDOWN_UNITS = {
  Days: 'Day(s)',
  Weeks: 'Week(s)',
  Months: 'Month(s)',
  Years: 'Year(s)',
};

/**
 *  This lives here because it causes circular dependecy for the default date values!
 *
 * Converts a given Date object to a Kwello date string format. Kwello dates take the actuald Date value, and Zero it WITHOUT converting to ISO.
 * ISO Conversion causes issues when selecting dates across timezones.
 *
 * @param date - The Date object to be converted.
 * @returns A string representing the date in Kwello format (yyyy-MM-ddTHH:mm:ss.sssZ).
 */
export const convertToKwelloDate = (date: Date): string => {
  return `${formatDate(date, 'yyyy-MM-dd')}T00:00:00.000`;
};

export const DEFAULT_START_DATE = convertToKwelloDate(subMonths(new Date(), 6));
export const DEFAULT_END_DATE = convertToKwelloDate(new Date());
export const DEFAULT_FILTER_DATE_LABEL: VisualData = [6, DATE_DROPDOWN_UNITS.Months];
export const DEFAULT_FILTER_VISUAL_DATA_TYPE = DateFilterTab.LAST;
export const DEFAULT_PLANNING_START_DATE = convertToKwelloDate(new Date());
export const DEFAULT_PLANNING_END_DATE = convertToKwelloDate(addMonths(new Date(), 3));
export const DEFAULT_PLANNING_FILTER_DATE_LABEL: VisualData = [3, DATE_DROPDOWN_UNITS.Months];
export const DEFAULT_PLANNING_FILTER_VISUAL_DATA_TYPE = DateFilterTab.NEXT;

export const DATE_FILTER_INITIAL_VALUE: DateFilter = {
  startDate: DEFAULT_START_DATE,
  endDate: DEFAULT_END_DATE,
  visualType: DEFAULT_FILTER_VISUAL_DATA_TYPE,
  visualData: DEFAULT_FILTER_DATE_LABEL,
  tempStartDate: DEFAULT_START_DATE,
  tempEndDate: DEFAULT_END_DATE,
  tempVisualType: DEFAULT_FILTER_VISUAL_DATA_TYPE,
  tempVisualData: DEFAULT_FILTER_DATE_LABEL,
  planningStartDate: DEFAULT_PLANNING_START_DATE,
  planningEndDate: DEFAULT_PLANNING_END_DATE,
  planningVisualType: DEFAULT_PLANNING_FILTER_VISUAL_DATA_TYPE,
  planningVisualData: DEFAULT_PLANNING_FILTER_DATE_LABEL,
  tempPlanningStartDate: DEFAULT_PLANNING_START_DATE,
  tempPlanningEndDate: DEFAULT_PLANNING_END_DATE,
  tempPlanningVisualType: DEFAULT_PLANNING_FILTER_VISUAL_DATA_TYPE,
  tempPlanningVisualData: DEFAULT_PLANNING_FILTER_DATE_LABEL,
};

export const DATASET_INITIAL_ID = 'INITIAL_ID';
export const TEAM_MEMBER_SESSION_KEY = 'coach_team_member_id';

export const API_PREFIX = {
  NEXTGEN: 'nextgen/api/',
  ELSIEAI: 'elsieai/',
  ELSIEAIV2: 'elsieaiapi/elsieai/',
  PLANNING: 'http://localhost:3001/',
  POWER_BI: '/api/',
};

/**
 * Join `API_BASE` to an `API_PREFIX` without producing a double slash.
 *
 * `VITE_API_BASE_URL` carries a trailing slash in every environment while the
 * prefixes are relative, so a hand-rolled `${API_BASE}/${prefix}` yields
 * `host//nextgen/api/`. RTK Query's `fetchBaseQuery` normalises that for us,
 * but the service-worker exports (Excel, PowerPoint) build their own absolute
 * URL and do not — they must use this helper.
 *
 * Azure APIM silently collapsed the doubled slash, which is why this never
 * surfaced before the migration. Kestrel does not, so on Railway the doubled
 * form returns 404.
 */
export const apiUrlFor = (prefix: string): string =>
  `${API_BASE.replace(/\/+$/, '')}/${prefix.replace(/^\/+/, '')}`;

interface FeatureExceptions {
  [key: string]: Feature[];
}

interface AggregateFeatureMap {
  [aggregateName: string]: Feature[];
}

export const COACH_VIEW_ITEM_FEATURES: FeatureExceptions = {
  ['Total Interactions']: [Feature.Discussions],
  ['Total Topics']: [Feature.Discussions],
  ['Topics / Interactions']: [Feature.Discussions],
};

export const AGGREGATE_FEATURES: AggregateFeatureMap = {
  DiscussionId: [Feature.Discussions],
};

export const PAGING_OPTIONS = [20, 50, 100];
export const PAGING_BUTTON_COUNT = 4;
export const LOGO_CLASS = 'kwello-logo';

export const DEFAULT_FILTER_VALIDATION_MESSAGE = 'This field is required.';

export const SLICE_FILTER_GLOBALS = ['ResponseOption'];

export const GLOBAL_LABELS: Record<string, string> = {
  conflicts: 'Conflicts',
  eventCapacity: 'Event Capacity',
  events: 'Events',
  interactions: 'Interactions',
  name: 'Name',
  profileCountry: 'Profile Country',
  totalEngagements: 'Total Engagements',
};

export const BRYNTUM_WIDGET_TYPES: WidgetType[] = [
  WidgetType.KWELLO_BRYNTUM_GRID,
  WidgetType.KWELLO_BRYNTUM_TREE_GRID,
];

export const CONVERTED_BRYNTUM_WIDGET_IDS: string[] = [
  'alignment_profile_question',
  'alignment_shift',
  'alignment_response_detail',
  'all-responses',
];

export const CONVERTED_BRYNTUM_WIDGET_TYPES: WidgetType[] = [
  WidgetType.ALIGNMENT_SHIFT,
  WidgetType.ALIGNMENT_RESPONSE_DETAIL,
  WidgetType.RESPONSE_DETAILS,
  WidgetType.ALIGNMENT_PROFILE_QUESTION,
];

export const CONVERTED_BRYNTUM_TREE_TYPES: WidgetType[] = [
  WidgetType.CATEGORY_RESPONSE_OVERVIEW,
  WidgetType.ALIGNMENT_OVERVIEW,
];

export const COUNTER_LABELS: { [key: string]: string } = {
  FieldTeamId: 'Team Members',
  ProfileId: 'Unique Profiles',
  ResponseId: 'Responses',
  InteractionId: 'Total Interactions',
  DiscussionId: 'Avg. Topics/Interactions',
  Duration: 'Avg. Duration (m)',
  SurveyId: 'Categories',
  Topic: 'Topics Discussed',
  Date: 'Latest Interaction Date',
};
